import React, { useState, useEffect } from 'react';
import { Calendar, ExternalLink, Info } from 'lucide-react';
import { availabilityData, propertyData } from '../data/mock';

const AvailabilitySection = () => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear] = useState(new Date().getFullYear());

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Generate calendar days (mock availability)
  const generateCalendarDays = (month, year) => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Add actual days with mock availability
    for (let day = 1; day <= daysInMonth; day++) {
      const isPast = new Date(year, month, day) < new Date();
      const isWeekend = new Date(year, month, day).getDay() % 6 === 0;
      // Mock: randomly mark some dates as booked
      const isBooked = Math.random() > 0.7 || isPast;
      
      days.push({
        day,
        isBooked,
        isPast,
        isWeekend
      });
    }

    return days;
  };

  const calendarDays = generateCalendarDays(selectedMonth, selectedYear);

  return (
    <section id="availability" className="section-spacing">
      <div className="container-coastal">
        <div className="text-center mb-xl">
          <h2 className="section-title">Check Availability</h2>
          <p className="section-subtitle" style={{ maxWidth: '700px', margin: '0 auto' }}>
            View our availability calendar and book your perfect Maine getaway
          </p>
        </div>

        <div className="card-coastal" style={{ maxWidth: '800px', margin: '0 auto' }}>
          {/* Calendar Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--spacing-lg)',
            flexWrap: 'wrap',
            gap: 'var(--spacing-md)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Calendar size={24} color="var(--color-ocean-deep)" />
              <h3 className="card-title" style={{ margin: 0 }}>
                {monthNames[selectedMonth]} {selectedYear}
              </h3>
            </div>

            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: 'var(--border-radius)',
                border: '2px solid var(--color-coastal-fog)',
                fontSize: '1rem',
                fontFamily: 'var(--font-body)',
                cursor: 'pointer',
                background: 'white'
              }}
              dangerouslySetInnerHTML={{
                __html: monthNames.map((month, idx) => 
                  `<option value="${idx}">${month}</option>`
                ).join('')
              }}
            />
          </div>

          {/* Calendar Grid */}
          <div style={{ marginBottom: 'var(--spacing-lg)' }}>
            {/* Day headers */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: '0.5rem',
              marginBottom: '0.5rem'
            }}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div
                  key={day}
                  style={{
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    color: 'var(--color-granite-gray)',
                    padding: '0.5rem'
                  }}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar days */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: '0.5rem'
            }}>
              {calendarDays.map((dayInfo, idx) => {
                if (!dayInfo) {
                  return <div key={`empty-${idx}`} />;
                }

                return (
                  <div
                    key={idx}
                    style={{
                      aspectRatio: '1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 'var(--border-radius)',
                      fontSize: '0.9375rem',
                      fontWeight: '500',
                      cursor: dayInfo.isBooked ? 'not-allowed' : 'pointer',
                      background: dayInfo.isBooked 
                        ? 'var(--color-coastal-fog)' 
                        : 'white',
                      border: dayInfo.isBooked 
                        ? '1px solid var(--color-coastal-fog)'
                        : '2px solid var(--color-ocean-deep)',
                      color: dayInfo.isBooked 
                        ? 'var(--color-harbor-mist)' 
                        : 'var(--color-ocean-deep)',
                      transition: 'all 0.2s ease',
                      opacity: dayInfo.isPast ? 0.4 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!dayInfo.isBooked) {
                        e.currentTarget.style.background = 'var(--color-ocean-deep)';
                        e.currentTarget.style.color = 'white';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!dayInfo.isBooked) {
                        e.currentTarget.style.background = 'white';
                        e.currentTarget.style.color = 'var(--color-ocean-deep)';
                      }
                    }}
                  >
                    {dayInfo.day}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 'var(--spacing-lg)',
            flexWrap: 'wrap',
            padding: 'var(--spacing-md)',
            background: 'var(--color-coastal-fog)',
            borderRadius: 'var(--border-radius)',
            marginBottom: 'var(--spacing-lg)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{
                width: '20px',
                height: '20px',
                border: '2px solid var(--color-ocean-deep)',
                borderRadius: 'var(--border-radius)'
              }} />
              <span className="small-text">Available</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{
                width: '20px',
                height: '20px',
                background: 'var(--color-coastal-fog)',
                border: '1px solid var(--color-harbor-mist)',
                borderRadius: 'var(--border-radius)'
              }} />
              <span className="small-text">Booked</span>
            </div>
          </div>

          {/* Info Box */}
          <div style={{
            padding: 'var(--spacing-md)',
            background: 'rgba(10, 61, 98, 0.05)',
            borderRadius: 'var(--border-radius)',
            borderLeft: '4px solid var(--color-ocean-deep)',
            marginBottom: 'var(--spacing-lg)'
          }}>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <Info size={20} color="var(--color-ocean-deep)" style={{ flexShrink: 0 }} />
              <div>
                <p className="body-text" style={{ fontSize: '0.9375rem' }}>
                  This calendar shows approximate availability. For real-time availability 
                  and instant booking, please visit our Airbnb listing.
                </p>
              </div>
            </div>
          </div>

          {/* Book Button */}
          <div style={{ textAlign: 'center' }}>
            <a
              href={propertyData.airbnbUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '1.125rem'
              }}
            >
              Book on Airbnb <ExternalLink size={20} />
            </a>
            <p className="small-text mt-sm">
              Secure booking with flexible cancellation options
            </p>
          </div>
        </div>

        {/* Quick Info Cards */}
        <div className="grid-3 mt-xl">
          <div className="card-coastal text-center">
            <h4 className="card-title mb-sm" style={{ fontSize: '1.125rem' }}>
              Minimum Stay
            </h4>
            <p className="body-text">2 nights (3 nights on holidays)</p>
          </div>
          <div className="card-coastal text-center">
            <h4 className="card-title mb-sm" style={{ fontSize: '1.125rem' }}>
              Check-in / Check-out
            </h4>
            <p className="body-text">3:00 PM / 11:00 AM</p>
          </div>
          <div className="card-coastal text-center">
            <h4 className="card-title mb-sm" style={{ fontSize: '1.125rem' }}>
              Instant Booking
            </h4>
            <p className="body-text">Available for qualified guests</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AvailabilitySection;
